import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { Edit02Icon, FloppyDiskIcon } from '@hugeicons-pro/core-bulk-rounded/index'
import { Cancel01Icon } from '@hugeicons-pro/core-twotone-rounded/index'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import SaveNotesDialog from './SaveNotesDialog'

const ItemNotesModal = ({ handleUpdate, onClose, open, orderItem }) => {
    const { t } = useTranslation()

    const [isEditingCustomerNotes, setIsEditingCustomerNotes] = useState(false)

    const [itemCustomerNotes, setItemCustomerNotes] = useState(orderItem?.customerNotes)
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)

    const itemVendorNotes = orderItem?.vendorNotes
    const name = orderItem?.name

    const toggleEditVendorNotes = () => {
        const baseObj = orderItem

        if (isEditingCustomerNotes) {
            handleUpdate({ ...baseObj, customerNotes: itemCustomerNotes })
        }
        setIsEditingCustomerNotes((v) => !v)
    }

    const handleCustomerNotesChange = (event) => {
        const { value } = event.target
        if (value != itemCustomerNotes) {
            setItemCustomerNotes(value)
        }
    }

    const handleClose = (_, reason) => {
        if (reason !== 'backdropClick') {
            if (isEditingCustomerNotes) {
                setShowConfirmationDialog(true)
            } else {
                onClose()
            }
        }
    }

    const onSaveNotesDialogCancel = () => {
        setShowConfirmationDialog(false)
        onClose()
    }

    const onSaveNotesDialogConfirm = () => {
        const baseObj = orderItem
        setShowConfirmationDialog(false)
        handleUpdate({ ...baseObj, customerNotes: itemCustomerNotes })
        onClose()
    }

    useEffect(() => {
        setItemCustomerNotes(orderItem?.customerNotes)

        return () => {
            setItemCustomerNotes('')
        }
    }, [orderItem])

    return (
        <Dialog
            open={open}
            PaperProps={{ sx: { width: 400 } }}
            disableEscapeKeyDown
            onClose={handleClose}
        >
            <DialogTitle
                alignItems="center"
                data-testid="order-item-notes-modal"
                display="flex"
                justifyContent="space-between"
            >
                <Typography
                    sx={{
                        textTransform: 'uppercase',
                    }}
                    variant="strong1"
                >
                    {t('NOTES FOR')} {name}
                </Typography>
                <IconButton
                    color="secondary"
                    size="small"
                    onClick={handleClose}
                >
                    <HugeiconsIcon icon={Cancel01Icon} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}
                >
                    {orderItem ? (
                        <Box>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    data-testid="vendor-notes-label"
                                    variant="strong2"
                                >
                                    {t('Customer notes')}
                                </Typography>
                                <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={toggleEditVendorNotes}
                                >
                                    <HugeiconsIcon
                                        altIcon={FloppyDiskIcon}
                                        icon={Edit02Icon}
                                        showAlt={isEditingCustomerNotes}
                                        size="18"
                                    />
                                </IconButton>
                            </Box>

                            <TextField
                                color="secondary"
                                data-testid="customer-notes"
                                helperText={t('Optionally add notes about this part.')}
                                minRows={3}
                                placeholder={t('No customer notes saved on this part')}
                                slotProps={{
                                    input: {
                                        readOnly: !isEditingCustomerNotes,
                                        style: { fontSize: 14 },
                                    },
                                }}
                                value={itemCustomerNotes}
                                fullWidth
                                multiline
                                onChange={handleCustomerNotesChange}
                            />
                        </Box>
                    ) : null}
                    {itemVendorNotes ? (
                        <Box>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    data-testid="vendor-notes-label"
                                    variant="strong2"
                                >
                                    {t('Vendor notes')}
                                </Typography>
                            </Box>
                            <TextField
                                color="secondary"
                                data-testid="vendor-notes"
                                helperText={t('Vendor notes about this part.')}
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        style: { fontSize: 14 },
                                    },
                                }}
                                value={itemVendorNotes}
                                fullWidth
                                multiline
                            />
                        </Box>
                    ) : null}
                </Box>
            </DialogContent>
            <SaveNotesDialog
                open={showConfirmationDialog}
                onCancel={onSaveNotesDialogCancel}
                onConfirm={onSaveNotesDialogConfirm}
            />
        </Dialog>
    )
}

ItemNotesModal.propTypes = {
    handleUpdate: PropTypes.func,
    miscItem: PropTypes.object,
    open: PropTypes.bool,
    orderItem: PropTypes.object,
    onClose: PropTypes.func,
}

export default ItemNotesModal
